import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { BAR_TYPES, WEEKS, type BarProps, type BarType } from "@/lib/roadmapDom"

export interface RoadmapBarState {
  el: Element
  props: BarProps
  isNew: boolean
}

const weekLabel = (i: number) => `W${i}`

/** Modal editor for a roadmap bar: rich text, the week span (start/end column)
 *  and the type/colour. */
export function RoadmapBarModal({
  state,
  onApply,
  onCancel,
  onDelete,
}: {
  state: RoadmapBarState
  onApply: (p: BarProps) => void
  onCancel: () => void
  onDelete: () => void
}) {
  const { props, isNew } = state
  const textRef = useRef<HTMLDivElement>(null)
  const [start, setStart] = useState(props.startWeek)
  const [end, setEnd] = useState(props.endWeek)
  const [type, setType] = useState<BarType>(props.type)

  useEffect(() => {
    if (textRef.current) textRef.current.innerHTML = props.html
  }, [props.html])

  const apply = () =>
    onApply({ html: textRef.current?.innerHTML ?? "", startWeek: start, endWeek: Math.max(start, end), type })

  const weekOpts = Array.from({ length: WEEKS }, (_, i) => (
    <option key={i} value={i}>
      {weekLabel(i)}
    </option>
  ))

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="w-full max-w-md space-y-3 rounded-xl bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">{isNew ? "New bar" : "Edit bar"}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            ✕
          </button>
        </div>

        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Text
          <div
            ref={textRef}
            contentEditable
            suppressContentEditableWarning
            className="mt-1 min-h-[38px] rounded-md border border-slate-300 px-2 py-1.5 text-sm font-normal normal-case tracking-normal text-slate-800 focus:outline-none focus:ring-2 focus:ring-header/40"
          />
        </label>

        <div className="flex flex-wrap gap-3">
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Start week
            <select value={start} onChange={(e) => setStart(Number(e.target.value))} className="rounded-md border border-slate-300 px-2 py-1 text-xs font-normal normal-case tracking-normal text-slate-700">
              {weekOpts}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            End week
            <select value={end} onChange={(e) => setEnd(Number(e.target.value))} className="rounded-md border border-slate-300 px-2 py-1 text-xs font-normal normal-case tracking-normal text-slate-700">
              {weekOpts}
            </select>
          </label>
          <label className="flex flex-1 flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Type
            <select value={type} onChange={(e) => setType(e.target.value as BarType)} className="rounded-md border border-slate-300 px-2 py-1 text-xs font-normal normal-case tracking-normal text-slate-700">
              {BAR_TYPES.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {end < start && <p className="text-[11px] text-amber-700">End week is before start — it’ll be clamped.</p>}

        <div className="flex items-center justify-between pt-1">
          <button onClick={onDelete} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50">
            Delete
          </button>
          <div className="flex gap-2">
            <button onClick={onCancel} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={apply} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700">
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
